import {
  Dialog,
  type DialogContentProps,
  type DialogOverlayProps,
  type DialogPortalProps,
  type DialogRootOptions,
} from "@kobalte/core/dialog";
import { createPresence } from "@solid-primitives/presence";
import { type JSX, type Signal, createSignal, splitProps } from "solid-js";
import { twMerge } from "tailwind-merge";
import { AnimateChildren } from "../animation/AnimateChildren";

export const StDialog = (
  props: {
    children?: JSX.Element;
    content: JSX.Element;
    portalProps?: Omit<DialogPortalProps, "children">;
    overlayProps?: DialogOverlayProps & JSX.HTMLAttributes<HTMLDivElement>;
    contentProps?: DialogContentProps & JSX.HTMLAttributes<HTMLDivElement>;
  } & DialogRootOptions,
) => {
  const signal = props.onOpenChange ? ([() => props.open, props.onOpenChange] as Signal<boolean>) : createSignal(false);
  const [open, setOpen] = signal;

  const duration = 200;
  const presence = createPresence(open, {
    transitionDuration: duration,
  });

  const [split, rest] = splitProps(props, ["content", "portalProps", "overlayProps", "contentProps"]);
  return (
    <Dialog {...rest} onOpenChange={setOpen} open={open()} forceMount={presence.isMounted()}>
      {rest.children}

      <Dialog.Portal {...split.portalProps}>
        <AnimateChildren
          show={open()}
          duration={duration}
          presence={presence}
          enter={{
            opacity: [0, 1],
          }}
          exit={{
            opacity: 0,
          }}
        >
          {(overlayAnim) => (
            <Dialog.Overlay
              {...split.overlayProps}
              ref={overlayAnim.ref}
              class={twMerge("fixed z-90 inset-0 bg-black/50 dark:bg-white/10", split.overlayProps?.class)}
            />
          )}
        </AnimateChildren>

        <AnimateChildren
          show={open()}
          duration={duration}
          presence={presence}
          enter={{
            opacity: [0, 1],
            y: [10, 0],
          }}
          exit={{
            opacity: 0,
            y: 10,
          }}
        >
          {(contentAnim) => (
            <div class="fixed z-90 inset-0 flex items-center justify-center pointer-events-none">
              <Dialog.Content
                {...split.contentProps}
                ref={contentAnim.ref}
                class={twMerge(
                  "max-h-[80vh] relative w-screen text-primary-text max-w-xl bg-panel overflow-y-auto rounded-xl p-8 pointer-events-auto",
                  split.contentProps?.class,
                )}
              >
                {split.content}
              </Dialog.Content>
            </div>
          )}
        </AnimateChildren>
      </Dialog.Portal>
    </Dialog>
  );
};

export const StDialogTrigger = Dialog.Trigger;
export const StDialogTitle = Dialog.Title;
export const StDialogDescription = Dialog.Description;
export const StDialogCloseButton = Dialog.CloseButton;
